import { useTracking } from '@vp/react-tracking';
import React from 'react';
import { Card, FlexBox, Typography, responsive } from '@vp/swan';
import { Request } from '@99designs/graph-utils/types';
import { __ } from '@99designs/i18n';
import { DS_BRIEF_MULTI_STEP_URL } from '@99designs/i18n';
import { CTA, RequestCardCTA } from './RequestCardCTA';
import { RequestCardImage } from './RequestCardImage';
import { RequestCardSummary } from './RequestCardSummary';

const ResponsiveFlexBox = responsive(FlexBox);

interface DraftBriefCardProps {
    request: Request;
}

const DraftBriefCard: React.FC<DraftBriefCardProps> = ({ request }) => {
    const { tracking } = useTracking();

    const brief = request.brief;
    if (!brief) {
        return null;
    }

    const briefUrl = `${DS_BRIEF_MULTI_STEP_URL}/${brief.id}?step=1`;
    const printProductName = request.expertService?.printProduct?.name;
    const title =
        brief.title || __(`Untitled ${printProductName} design project`, { printProductName });

    const analytics = {
        section: 'draft',
        category: 'Design Services',
        pageName: 'Design Services:Dashboard',
        pageSection: 'Design Services',
        pageStage: 'My Account',
    };

    const ctas: CTA[] = [
        {
            label: __('Complete brief'),
            iconType: 'edit',
            href: briefUrl,
            onClick: () => {
                tracking.track('Navigation Clicked', {
                    ...analytics,
                    action: 'Navigation Clicked',
                    navigationDetail: 'complete-brief',
                });
            },
            openInNewTab: false,
            skin: 'primary',
        },
        {
            label: __('Delete brief'),
            iconType: 'delete',
            onClick: () => {
                tracking.track('Action Clicked', {
                    ...analytics,
                    action: 'Action Clicked',
                    actionDetail: 'delete-brief',
                });
            },
            openInNewTab: false,
            skin: 'tertiary',
        },
    ];

    return (
        <Card bordered>
            <ResponsiveFlexBox
                xs={{
                    flexDirection: 'column',
                    gap: 'between-subsections',
                    alignItems: 'center',
                }}
                sm={{
                    flexDirection: 'row',
                    gap: 'between-sections',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                }}
            >
                {/* Project image thumbnail */}
                <RequestCardImage
                    imgSrc="https://cms.cloudinary.vpsvc.com/image/upload/fl_sanitize,q_auto/expert_services/Dashboard/Draft_brief_graphic_617_570.svg"
                    imgAlt={__('Draft Brief')}
                />

                {/* Project details */}
                <ResponsiveFlexBox
                    style={{ flex: '1 1 100%' }}
                    xs={{
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 'between-actions',
                    }}
                    sm={{ alignItems: 'flex-start' }}
                >
                    <RequestCardSummary
                        title={title}
                        linkUrl={briefUrl}
                        createdAt={brief.createdAt}
                        productName={printProductName}
                        analytics={analytics}
                        status="DRAFT_BRIEF_REQUEST_IN_PROGRESS"
                    />
                    <Typography fontSkin="body-standard">{brief.description}</Typography>
                </ResponsiveFlexBox>
                {/* Project CTA */}
                <RequestCardCTA ctas={ctas} />
            </ResponsiveFlexBox>
        </Card>
    );
};

export { DraftBriefCard };
